/**
 * The PWA app icon badge.
 *
 * `navigator.setAppBadge` is a progressive enhancement in the strictest sense: it is
 * absent on iOS Safari and on every desktop browser except Chromium, and it throws
 * rather than no-ops on some engines that expose it but refuse the permission. None of
 * that may reach the caller — a badge is decoration on top of a count the header
 * already shows, so failing to set one must never surface as an error.
 *
 * Only meaningful for an installed PWA. In a browser tab the call succeeds and shows
 * nothing, which is the correct outcome and needs no branch.
 */
// Structurally typed rather than an interface extending Navigator: the DOM lib already
// declares these in newer TypeScript, and re-declaring them as optional conflicts. This
// says only "it might have them", which is the actual situation at runtime.
type BadgeCapableNavigator = {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

export function setAppBadge(count: number): void {
  if (typeof navigator === 'undefined') return;

  const nav = navigator as unknown as BadgeCapableNavigator;

  try {
    // Zero clears rather than showing a "0" — `clearAppBadge` is the documented way,
    // and `setAppBadge(0)` is specified to clear too but is not honoured everywhere.
    const promise = count > 0 ? nav.setAppBadge?.(count) : nav.clearAppBadge?.();
    // Rejects when the document is not fully active, or when the OS refuses. Swallowed
    // for the same reason as the throw below.
    void promise?.catch(() => undefined);
  } catch {
    // Engine exposes the method but refuses the call. Nothing to do and nothing worth
    // telling anyone: the number is already on screen.
  }
}
